import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowRightToBracket, viPlus } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function InboundList() {
  const { did } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const inboundStore = useSelector((state) => state.domain.inboundRoutes);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time inboundList or searchQuery changes
  const searchResults = useSearch(inboundStore.list, searchValue, ["name", "did"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getInbounds()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!did && matchesMdUp && inboundStore.meta.lastFetched) {
      if (inboundStore.list.length >= 1) {
        navigate(`${inboundStore.list[0].did}`);
      } else {
        navigate("new");
      }
    }
  }, [matchesMdUp, navigate, inboundStore.list, inboundStore.meta.lastFetched, did]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Inbound routes"
        aria-label="Inbound routes list"
        id="inbound-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => x.did,
          to: (x) => `../${x.did}`,
          selected: (x) => x.did === did,
          icon: viArrowRightToBracket,
        }}
        hideContentLoader={inboundStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: inboundStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any inbound routes yet",
          noResults: "No inbound routes in your current query",
          fetching: "Getting your inbound routes",
          icon: viArrowRightToBracket,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default InboundList;
